import { DataClass } from '@calm/evexia-web-common/dist/circle';
import {
    ConversationConversationTypeEnum,
    ConversationRequest,
} from '@calm/evexia-web-sdk';
import createAiRouterApi from '@rhg/libs/api/create-ai-router';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    invalidateQueryKey,
    queryKeys,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import { useActiveCircle } from '../use-active-circle';

const getConversationDataClass = (
    conversationType: ConversationConversationTypeEnum
): DataClass =>
    conversationType === ConversationConversationTypeEnum.Journaling
        ? 'ai_journaling'
        : 'ai_concierge';

interface useAiConversationParams {
    conversationType: ConversationConversationTypeEnum;
    pageSize?: number;
    shouldFilterCompleted?: boolean;
}

export function useAiConversation({
    conversationType,
    pageSize = 10,
    shouldFilterCompleted = false,
}: useAiConversationParams) {
    const {
        activeCircle: circle,
        hasPermission,
        isLoading: isActiveCircleLoading,
    } = useActiveCircle();
    // The backend always grants read, so create is what enables the feature
    const canAccessConversations = hasPermission(
        getConversationDataClass(conversationType),
        ['c', 'r']
    );
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const api = createAiRouterApi(getAccessTokenSilently);

    const isQueryEnabled =
        !isActiveCircleLoading &&
        canAccessConversations &&
        isAuthenticated &&
        typeof circle?.authorizationJWT === 'string' &&
        typeof circle?.recipient.id === 'number';

    const { data, ...otherProps } = useQuery({
        queryKey: [
            queryKeys.aiConversations,
            circle?.recipient.id,
            conversationType,
            pageSize,
        ],
        queryFn: async () => {
            const response = await api.aiRouterConversationsGetConversationsV1({
                conversationType,
                circleAuthorizationToken: circle!.authorizationJWT!,
                recipientId: circle!.recipient.id,
                pageSize,
                state: shouldFilterCompleted ? 'completed' : undefined,
            });

            return response.data;
        },
        enabled: isQueryEnabled,
    });

    return {
        // Disabling the query leaves its cached result in place, so the data
        // has to be gated too - otherwise it outlives the read access to it.
        ...(isQueryEnabled ? data : undefined),
        ...otherProps,
    };
}

/* Selects which usercrypt data the AI router includes when creating a conversation */
export type ConversationInclude = 'goals' | 'topics';

interface SaveAiConversationParams {
    conversationRequest: ConversationRequest;
    conversationType: ConversationConversationTypeEnum;
    include?: ConversationInclude;
}

export function useSaveAiConversation() {
    const { getAccessTokenSilently } = useAuth();
    const {
        activeCircle: circle,
        hasPermission,
        isLoading: isActiveCircleLoading,
    } = useActiveCircle();
    const api = createAiRouterApi(getAccessTokenSilently);

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            conversationRequest,
            conversationType,
            include,
        }: SaveAiConversationParams) => {
            const canCreateConversation = hasPermission(
                getConversationDataClass(conversationType),
                'c'
            );

            if (!isActiveCircleLoading && canCreateConversation) {
                const response =
                    await api.aiRouterConversationsCreateConversationByTypeV1({
                        circleAuthorizationToken: circle!.authorizationJWT!,
                        conversationRequest: conversationRequest,
                        conversationType,
                        ...(include && { include }),
                    });
                return response;
            } else {
                return;
            }
        },
    });
    return {
        saveConversation: mutate,
        saveConversationAsync: mutateAsync,
        ...otherProps,
    };
}

interface UseDeleteAiConversationParams {
    /** Circle data class whose delete permission gates the mutation. */
    dataClass: DataClass;
}

export function useDeleteAiConversation({
    dataClass,
}: UseDeleteAiConversationParams) {
    const { getAccessTokenSilently } = useAuth();
    const { activeCircle: circle, hasPermission } = useActiveCircle();
    const api = createAiRouterApi(getAccessTokenSilently);
    const canDeleteConversation = hasPermission(dataClass, 'd');

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (conversationId: string) => {
            // Rejects rather than no-ops: callers treat a resolved delete as
            // success and would confirm a conversation that still exists.
            if (!canDeleteConversation) {
                throw new Error(
                    `Missing ${dataClass} delete permission for this circle`
                );
            }

            const response = await api.aiRouterRecipientsDeleteConversationV1({
                recipientId: circle!.recipient.id,
                conversationId,
                circleAuthorizationToken: circle!.authorizationJWT!,
            });
            return response;
        },
        onSuccess: async () => {
            await invalidateQueryKey([
                queryKeys.aiConversations,
                circle!.recipient.id,
            ]);
        },
    });

    return {
        deleteConversation: mutate,
        deleteConversationAsync: mutateAsync,
        ...otherProps,
    };
}
